import type { ComponentConfigV2 } from '@/types/configurationV2'
import { COMPONENT_TYPE_INFO } from '@/types/configurationV2'
import { buildFlowPreview, type PreviewEntry, type WarmupRound } from './warmupPolicy'

const STATE_STYLE: Record<PreviewEntry['state'], { label: string; className: string }> = {
  required: { label: '필수', className: 'bg-blue-50 text-blue-700' },
  optional: { label: '선택', className: 'bg-amber-50 text-amber-700' },
  hidden: { label: '숨김', className: 'bg-gray-100 text-gray-400 line-through' },
}

const ROUNDS: { round: WarmupRound; title: string }[] = [
  { round: 'initial', title: '첫 학습' },
  { round: 'designatedReview', title: '지정 복습' },
]

interface Props {
  components: ComponentConfigV2[]
  branchLabel: string
}

/**
 * 학습 흐름 미리보기 — 선택한 분기(기본/override)의 컴포넌트가 첫 학습/지정 복습에서 어떤 순서·상태로
 * 제공되는지 보여준다. 편집 중인 초안 기준이며(발행본 아님), 특정 레슨을 모르므로 콘텐츠·직전 레슨 조건은 부가 설명으로만 표시한다.
 */
export default function LessonFlowPreview({ components, branchLabel }: Props) {
  return (
    <section className="rounded-lg border border-gray-200 bg-white p-3" data-testid="lesson-flow-preview">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <span className="text-sm font-bold">학습 흐름 미리보기</span>
          <span className="ml-2 text-xs text-gray-400">{branchLabel} · 편집 중인 설정 기준(발행 전)</span>
        </div>
        <span className="text-[11px] text-gray-400">
          콘텐츠 유무·직전 레슨 유무에 따라 실제 레슨에서는 달라질 수 있습니다
        </span>
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {ROUNDS.map(({ round, title }) => {
          const entries = buildFlowPreview(components, round)
          const visible = entries.filter((e) => e.state !== 'hidden')
          return (
            <div key={round} className="rounded-md border border-gray-100 bg-gray-50 p-2" data-round={round}>
              <div className="mb-1 flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-700">{title}</span>
                <span className="text-[11px] text-gray-400">{visible.length}단계 제공</span>
              </div>
              {entries.length === 0 ? (
                <p className="text-[11px] text-gray-400">컴포넌트가 없습니다.</p>
              ) : (
                <ol className="space-y-1">
                  {entries.map((entry, idx) => {
                    const info = COMPONENT_TYPE_INFO[entry.type]
                    const style = STATE_STYLE[entry.state]
                    const order = visible.indexOf(entry)
                    return (
                      <li key={`${entry.id}-${idx}`} className="flex items-center gap-2 text-xs">
                        <span className="w-4 text-right tabular-nums text-gray-400">
                          {order >= 0 ? order + 1 : '–'}
                        </span>
                        <span
                          className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-[10px] font-bold text-white"
                          style={{ backgroundColor: info.color, opacity: entry.state === 'hidden' ? 0.4 : 1 }}
                        >
                          {info.icon}
                        </span>
                        <span className={`min-w-0 flex-1 truncate ${entry.state === 'hidden' ? 'text-gray-400' : 'text-gray-800'}`}>
                          {entry.label}
                        </span>
                        {entry.note && <span className="text-[10px] text-gray-400">{entry.note}</span>}
                        <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${style.className}`}>
                          {style.label}
                        </span>
                      </li>
                    )
                  })}
                </ol>
              )}
            </div>
          )
        })}
      </div>
      <p className="mt-2 text-[11px] text-gray-500">
        새 정책은 다음에 시작되는 복습부터 적용되며, 진행 중인 복습 회차에는 새 의무가 추가되지 않습니다.
      </p>
    </section>
  )
}
